/**
 * LocationSettingsSection - Per-location production app configuration
 *
 * Allows admins to enable/disable production app for each location.
 * Disabled locations won't appear in Orders, Stations, or Dashboard.
 *
 * Features:
 * - List all locations with production toggle
 * - Confirmation dialog when disabling location with bins
 * - Empty/loading/error states
 * - Accessible with ARIA attributes
 */
import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { StatusPill } from '@/components/shared/StatusPill';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  MapPin,
  Loader2,
  AlertTriangle,
  RefreshCw,
  Package,
  ExternalLink,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { trpc, type RouterOutputs } from '@/lib/trpc';
import { toast } from 'sonner';
import { ArtworkPublicBucketField } from './ArtworkPublicBucketField';
import { TENCENT_COS_REGION_OPTIONS, type TencentCosRegion } from '../tencent-cos-regions';

/**
 * Inferred from the tRPC router output so future return-type widenings
 * (e.g. additional `print_file_source` values) propagate automatically
 * without a parallel local interface to maintain.
 *
 * Phase 47.02.2 Plan 03 cleanup: replaces a hand-rolled interface that
 * had to be widened separately when the backend mapper added 'gcs'
 * (Plan 03 Deviation #1). Multi-agent review recommended this inference
 * pattern to eliminate the entire deviation class.
 */
type LocationSetting = RouterOutputs['productionSettings']['getLocationSettings'][number];

/** Storage backends operators can write via the dropdown. Mirror of the
 * backend zod enum at `productionSettings.setLocationPrintFileSource`. */
type WritablePrintFileSource = 'azure' | 'tencent_cos' | 'gcs';

/** Phase 47.02.3-LITE — per-location Tencent COS region.
 * Now sourced from the shared `tencent-cos-regions` module (single source of
 * truth mirrored from the backend zod enum + DB CHECK), reused by both the
 * print-file region select below and the artwork-bucket region select, so the
 * two never drift. */
type TencentRegion = TencentCosRegion;

interface LocationSettingsSectionProps {
  /** When true, renders without Card wrapper for embedding in accordions */
  embedded?: boolean;
  /**
   * Console single-location mode: when set, render only this location's row
   * and omit the per-row location name (the Console section header already
   * names the location). Absent = today's full-list behavior.
   */
  locationId?: string;
}

export function LocationSettingsSection({ embedded = false, locationId }: LocationSettingsSectionProps) {
  const [confirmDisable, setConfirmDisable] = useState<LocationSetting | null>(null);
  const utils = trpc.useUtils();

  // Queries
  const locationsQuery = trpc.productionSettings.getLocationSettings.useQuery();
  // BLU-368: gate the `Tencent COS` option on credential presence (option-
  // disabled with hint, NOT trigger-disabled — DESIGN.md §15.5).
  const tencentSettingsQuery = trpc.productionSettings.getTencentCosSettings.useQuery();
  const hasTencentCredentials = tencentSettingsQuery.data?.hasCredentials === true;
  // Phase 47.02.3-LITE — surface the org-level Vault region so the
  // per-location picker's "Org default" option labels with the actual
  // fallback region (e.g. "Org default (ap-shanghai)") instead of an
  // unattributed italic.
  const orgDefaultRegion = tencentSettingsQuery.data?.region ?? null;
  // Phase 33-16: surface the org's BYO-GCS state in the GCS option subtitle
  // so admins can see at a glance whether GCS routes to their own bucket or
  // to the Blue Monk default. Without this, the dropdown was static text
  // and gave no feedback that BYO configuration had taken effect.
  const gcsSettingsQuery = trpc.productionSettings.getGcsSettings.useQuery();
  const gcsByoActive =
    gcsSettingsQuery.data?.hasCredentials === true && gcsSettingsQuery.data?.byoActive === true;
  const gcsByoBucket = gcsByoActive ? gcsSettingsQuery.data?.bucket ?? null : null;

  // Mutations
  const updateMutation = trpc.productionSettings.updateLocationProductionEnabled.useMutation({
    onSuccess: (data) => {
      toast.success(`Production ${data.enabled ? 'enabled' : 'disabled'} for location`);
      void locationsQuery.refetch();
      // LocationContext powers the production header and dashboard centre
      // cards through locations.list. Invalidate it alongside this settings
      // query so a newly disabled centre disappears immediately.
      void utils.locations.list.invalidate();
      setConfirmDisable(null);
    },
    onError: (error) => {
      toast.error(`Failed to update: ${error.message}`);
    },
  });

  // BLU-368: per-location print-file-source mutation (Plan 04 ships the proc).
  const setPrintFileSourceMutation = trpc.productionSettings.setLocationPrintFileSource.useMutation({
    onSuccess: () => {
      toast.success('Print file source updated');
      locationsQuery.refetch();
    },
    onError: (error) => {
      toast.error(`Failed to update print file source: ${error.message}`);
    },
  });

  // Phase 47.02.3-LITE: per-location Tencent region mutation.
  const setTencentRegionMutation = trpc.productionSettings.setLocationTencentRegion.useMutation({
    onSuccess: () => {
      toast.success('Tencent region updated');
      locationsQuery.refetch();
    },
    onError: (error) => {
      toast.error(`Failed to update Tencent region: ${error.message}`);
    },
  });

  const handlePrintFileSourceChange = (location: LocationSetting, next: WritablePrintFileSource) => {
    if (next === 'tencent_cos' && !hasTencentCredentials) {
      // Defense-in-depth: this path is only reachable if the option-disabled
      // gate is bypassed; the backend also rejects when source = tencent_cos
      // without creds.
      return;
    }
    setPrintFileSourceMutation.mutate({ locationId: location.id, printFileSource: next });
  };

  const handleTencentRegionChange = (location: LocationSetting, next: TencentRegion | '__default__') => {
    setTencentRegionMutation.mutate({
      locationId: location.id,
      tencentRegion: next === '__default__' ? null : next,
    });
  };

  const handleToggle = (location: LocationSetting, enabled: boolean) => {
    // If disabling and location has bins, show confirmation
    if (!enabled && location.binCount > 0) {
      setConfirmDisable(location);
      return;
    }

    // Otherwise, update directly
    updateMutation.mutate({
      locationId: location.id,
      enabled,
    });
  };

  const confirmDisableLocation = () => {
    if (confirmDisable) {
      updateMutation.mutate({
        locationId: confirmDisable.id,
        enabled: false,
      });
    }
  };

  const isLoading = locationsQuery.isLoading;
  const hasError = locationsQuery.isError;
  const allLocations = locationsQuery.data || [];
  const locations = locationId ? allLocations.filter((l) => l.id === locationId) : allLocations;

  // Content to render (shared between embedded and standalone modes)
  const content = (
    <div className="space-y-4">
          {/* Error State */}
          {hasError && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Failed to load locations</AlertTitle>
              <AlertDescription className="flex items-center gap-2">
                {locationsQuery.error?.message || 'Unknown error'}
                <Button
                  variant="link"
                  className="p-0 h-auto"
                  onClick={() => locationsQuery.refetch()}
                >
                  <RefreshCw className="h-3 w-3 mr-1" />
                  Retry
                </Button>
              </AlertDescription>
            </Alert>
          )}

          {/* Loading State */}
          {isLoading && (
            <div className="flex items-center justify-center py-8" role="status" aria-busy="true">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" aria-hidden="true" />
              <span className="ml-2 text-muted-foreground">Loading locations...</span>
            </div>
          )}

          {/* Empty State */}
          {!isLoading && !hasError && locations.length === 0 && (
            <div className="flex flex-col items-center py-8 text-center bg-muted/30 rounded-lg">
              <MapPin className="h-10 w-10 text-muted-foreground/50 mb-3" />
              <p className="font-medium">No locations configured</p>
              <p className="text-sm text-muted-foreground mb-4">
                Add locations in IMS Settings to use the Production App
              </p>
              <Button variant="outline" asChild className="h-12 gap-2">
                <a href="/settings?tab=locations">
                  <ExternalLink className="h-4 w-4" />
                  Go to IMS Settings
                </a>
              </Button>
            </div>
          )}

          {/* Location List */}
          {!isLoading && !hasError && locations.length > 0 && (
            <div className="space-y-3" role="list" aria-label="Location production settings">
              {locations.map((location, index) => (
                <div key={location.id}>
                  <div className="overflow-hidden rounded-lg bg-muted/30" role="listitem">
                    <div className="flex items-center justify-between p-3 transition-colors hover:bg-muted/50">
                    <div className="flex items-center gap-3">
                      <div>
                        {/* Single-location mode labels the SETTING instead of
                            the location (named in the section header) so the
                            enable Switch reads as an inline row control. */}
                        <p className="font-medium">
                          {locationId ? 'Production enabled' : location.name}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <StatusPill tone={location.productionEnabled ? 'on' : 'off'}>
                            {location.productionEnabled ? 'Active' : 'Disabled'}
                          </StatusPill>
                          <span className="text-xs text-muted-foreground flex items-center gap-1">
                            <Package className="h-3 w-3" />
                            {location.binCount} bins
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      {/* BLU-368 Plan 47.02-05: Print file source — only shown
                          when production is enabled. Dropdown sits left of the
                          on/off Switch (admin-side h-8 controls — this is
                          inventory-app within accordion, NOT production
                          credential surface). */}
                      {location.productionEnabled && (
                        <div className="flex flex-wrap items-center justify-end gap-x-2 gap-y-1">
                          <span className="text-xs text-muted-foreground/90">Print file source</span>
                          <Select
                            value={location.printFileSource}
                            onValueChange={(v) => handlePrintFileSourceChange(location, v as WritablePrintFileSource)}
                            // Phase 47.02.3-LITE: lock during either mutation
                            // so the operator can't flip source mid-region-
                            // write (would leave the location in a transient
                            // (gcs + tencent_region) state until the next
                            // refetch).
                            disabled={setPrintFileSourceMutation.isPending || setTencentRegionMutation.isPending}
                          >
                            <SelectTrigger
                              className="h-8 w-[220px] text-xs"
                              aria-label={`Print file source for ${location.name}`}
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {/* Phase 47.02.2 Plan 04: GCS is the default for new locations
                                  (Blue Monk-hosted, no creds). Azure is now legacy — kept for
                                  Threadheads' existing locations until backfill. Tencent COS
                                  remains the China-region opt-in, gated on credential presence.
                                  `textValue` props ensure the trigger renders only the primary
                                  label rather than concatenating both lines via Radix's default
                                  text-content extraction. */}
                              <SelectItem value="gcs" textValue="Google Cloud Storage">
                                <div className="flex flex-col">
                                  <span>Google Cloud Storage</span>
                                  <span className="text-[10px] text-muted-foreground">
                                    {gcsByoBucket
                                      ? `Your bucket · ${gcsByoBucket}`
                                      : 'Default · PrintRun hosted'}
                                  </span>
                                </div>
                              </SelectItem>
                              <SelectItem value="azure" textValue="Azure">
                                <div className="flex flex-col">
                                  <span>Azure</span>
                                  <span className="text-[10px] text-muted-foreground">
                                    Legacy
                                  </span>
                                </div>
                              </SelectItem>
                              <SelectItem
                                value="tencent_cos"
                                disabled={!hasTencentCredentials}
                                textValue="Tencent COS"
                              >
                                <div className="flex flex-col">
                                  <span>Tencent COS</span>
                                  <span className="text-[10px] text-muted-foreground">
                                    {hasTencentCredentials ? 'China only' : 'Setup required'}
                                  </span>
                                </div>
                              </SelectItem>
                              {!hasTencentCredentials && (
                                <div className="border-t px-2 py-1.5">
                                  <Link
                                    to="/production/settings?tab=advanced#tencent-cos-accordion"
                                    className="text-xs text-primary hover:underline inline-flex items-center gap-1"
                                  >
                                    Open Tencent settings
                                    <ExternalLink className="h-3 w-3" />
                                  </Link>
                                </div>
                              )}
                            </SelectContent>
                          </Select>
                          {/* Phase 47.02.3-LITE — per-location Tencent region.
                              Only shown when the location actually routes to
                              Tencent. "Org default" maps to NULL so the factory
                              falls back to the Vault-stored region for legacy
                              single-region orgs. */}
                          {location.printFileSource === 'tencent_cos' && (
                            <Select
                              value={location.tencentRegion ?? '__default__'}
                              onValueChange={(v) => handleTencentRegionChange(location, v as TencentRegion | '__default__')}
                              // Disable while EITHER mutation is in flight so an
                              // operator can't race-flip source from tencent → gcs
                              // mid-region-write (the backend would reject the
                              // region write, but the UI shouldn't invite the
                              // attempt).
                              disabled={setTencentRegionMutation.isPending || setPrintFileSourceMutation.isPending}
                            >
                              <SelectTrigger
                                className="h-8 w-[220px] text-xs"
                                aria-label={`Tencent region for ${location.name}`}
                              >
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__default__" textValue="Org default">
                                  <span className="italic text-muted-foreground">
                                    Org default{orgDefaultRegion ? ` (${orgDefaultRegion})` : ''}
                                  </span>
                                </SelectItem>
                                {TENCENT_COS_REGION_OPTIONS.map((opt) => (
                                  <SelectItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        </div>
                      )}
                      <Switch
                        checked={location.productionEnabled}
                        onCheckedChange={(checked) => handleToggle(location, checked)}
                        disabled={updateMutation.isPending}
                        aria-label={`${location.productionEnabled ? 'Disable' : 'Enable'} production for ${location.name}`}
                      />
                    </div>
                    </div>
                    {/* BLU-662: dedicated PUBLIC artwork bucket for standard-catalogue
                        variant-artwork uploads (owner-org surface). Only meaningful on a
                        public-bucket backend (gcs / tencent_cos); azure has no public-bucket
                        concept, and it only matters once the location is production-enabled. */}
                    {location.productionEnabled && location.printFileSource !== 'azure' && (
                      <div className="border-t border-border/60 px-3 py-3">
                        <ArtworkPublicBucketField
                          location={location}
                          onSaved={() => locationsQuery.refetch()}
                        />
                      </div>
                    )}
                  </div>
                  {index < locations.length - 1 && <Separator className="my-2" />}
                </div>
              ))}
            </div>
          )}

          {/* Info about IMS locations */}
          {!isLoading && !hasError && locations.length > 0 && (
            <p className="text-xs text-muted-foreground pt-2">
              To add or remove locations, go to{' '}
              <a href="/settings?tab=locations" className="underline hover:text-foreground">
                IMS Settings - Locations
              </a>
            </p>
          )}

      {/* ARIA live region */}
      <div role="status" aria-live="polite" className="sr-only">
        {updateMutation.isSuccess && 'Location setting updated successfully'}
      </div>
    </div>
  );

  // If embedded, just return the content
  if (embedded) {
    return (
      <>
        {content}

        {/* Confirmation Dialog for Disabling Location with Bins */}
        <AlertDialog open={!!confirmDisable} onOpenChange={() => setConfirmDisable(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                Disable production for {confirmDisable?.name}?
              </AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div>
                  <p>
                    This location has <strong>{confirmDisable?.binCount} bins</strong> configured.
                    Disabling production will:
                  </p>
                  <ul className="list-disc ml-6 mt-2 space-y-1">
                    <li>Hide this location from the Production App</li>
                    <li>Existing bins and their contents will remain in the database</li>
                    <li>Orders assigned to this location won't appear in Production Queue</li>
                  </ul>
                  <p className="mt-2">
                    You can re-enable production at any time.
                  </p>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={updateMutation.isPending}>
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={confirmDisableLocation}
                disabled={updateMutation.isPending}
                className="bg-destructive hover:bg-destructive/90"
              >
                {updateMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : null}
                Disable Production
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </>
    );
  }

  // Standalone mode with Card wrapper
  return (
    <>
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <MapPin className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
            <CardTitle className="text-lg">Location Production Settings</CardTitle>
          </div>
          <CardDescription>
            Enable or disable the Production App for each location. Disabled locations won't appear
            in Orders, Stations, or Dashboard.
          </CardDescription>
        </CardHeader>

        <CardContent>
          {content}
        </CardContent>
      </Card>

      {/* Confirmation Dialog for Disabling Location with Bins */}
      <AlertDialog open={!!confirmDisable} onOpenChange={() => setConfirmDisable(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Disable production for {confirmDisable?.name}?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                <p>
                  This location has <strong>{confirmDisable?.binCount} bins</strong> configured.
                  Disabling production will:
                </p>
                <ul className="list-disc ml-6 mt-2 space-y-1">
                  <li>Hide this location from the Production App</li>
                  <li>Existing bins and their contents will remain in the database</li>
                  <li>Orders assigned to this location won't appear in Production Queue</li>
                </ul>
                <p className="mt-2">
                  You can re-enable production at any time.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={updateMutation.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDisableLocation}
              disabled={updateMutation.isPending}
              className="bg-destructive hover:bg-destructive/90"
            >
              {updateMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              Disable Production
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
